import { useState } from "react";
import axios from "axios";
import { Mail, CheckCircle2, Loader2 } from "lucide-react";
import { validateEmail } from "../utils/validators";

const getApiUrl = () => {
  if (typeof window !== "undefined") {
    const h = window.location.hostname;
    if (h === "sportsguru247.com" || h.includes("deploy.emergentcf.cloud") || h.includes("sportsguru")) return "/api";
  }
  return process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";
};
const API = getApiUrl();

/**
 * Compact, inline newsletter capture form.
 * `source` lets us know which page/component captured the lead (footer, sidebar, etc.).
 */
export default function NewsletterForm({ source = "footer", className = "" }) {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    const validation = validateEmail(email);
    if (validation) { setErr(validation); return; }
    setSubmitting(true); setErr(null);
    try {
      const path = typeof window !== "undefined" ? window.location.pathname : "/";
      await axios.post(`${API}/newsletter/subscribe`, { email, source: `${source}:${path}` });
      setDone(true);
    } catch {
      setErr("Could not subscribe right now. Try again later.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-emerald-500/20 border border-emerald-400/40 text-emerald-300 text-sm font-bold ${className}`} data-testid="newsletter-success">
        <CheckCircle2 className="w-4 h-4" /> You're in. Welcome aboard!
      </div>
    );
  }

  return (
    <form onSubmit={submit} className={`flex flex-col sm:flex-row gap-2 w-full ${className}`} data-testid="newsletter-form">
      <div className="relative flex-1">
        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <input
          type="email"
          value={email}
          onChange={(e) => { setEmail(e.target.value); if (err) setErr(null); }}
          placeholder="Your email — daily picks & WC alerts"
          aria-invalid={!!err}
          data-testid="newsletter-email"
          className={`w-full pl-9 pr-4 py-2.5 bg-slate-800/70 border rounded-full text-sm text-white placeholder:text-slate-500 focus:outline-none ${err ? "border-rose-400" : "border-slate-700 focus:border-emerald-400"}`}
        />
      </div>
      <button
        type="submit"
        disabled={submitting}
        data-testid="newsletter-submit"
        className="px-5 py-2.5 rounded-full bg-gradient-to-r from-emerald-400 to-emerald-500 text-slate-950 font-extrabold text-sm hover:scale-[1.02] transition-transform shadow-lg shadow-emerald-500/25 disabled:opacity-60 inline-flex items-center justify-center gap-1.5"
      >
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
        Subscribe
      </button>
      {err && <div className="text-rose-400 text-xs sm:absolute sm:mt-12" data-testid="newsletter-error">{err}</div>}
    </form>
  );
}
